import type { InteractionEvent } from '../interaction/types';

/**
 * Tool modes — determines how a tool responds to user input.
 */
export enum ToolMode {
    /** Responds to bound mouse button events. Creates new annotations. */
    ACTIVE = 'Active',
    /** Receives mouseMove for hover. Existing annotations are selectable but tool won't create new ones. */
    PASSIVE = 'Passive',
    /** Renders visually but doesn't respond to input. */
    ENABLED = 'Enabled',
    /** Completely inactive, not rendered. */
    DISABLED = 'Disabled',
}

/**
 * Mouse button bitmask values (matches PointerEvent.buttons).
 */
export enum MouseButton {
    PRIMARY = 1, // Left
    SECONDARY = 2, // Right
    AUXILIARY = 4, // Middle
    /** Special value for scroll wheel events (not a real button bitmask). */
    WHEEL = 512,
}

/**
 * Modifier key combinations for tool bindings.
 */
export enum ModifierKey {
    NONE = '',
    SHIFT = 'Shift',
    CTRL = 'Ctrl',
    ALT = 'Alt',
    META = 'Meta',
    SHIFT_CTRL = 'ShiftCtrl',
    SHIFT_ALT = 'ShiftAlt',
    CTRL_ALT = 'CtrlAlt',
}

/**
 * A single tool binding: which mouse button + modifier triggers this tool.
 */
export interface ToolBinding {
    mouseButton: MouseButton;
    modifierKey?: ModifierKey;
}

/**
 * Options passed when setting a tool to Active mode.
 */
export interface ToolBindingOptions {
    bindings: ToolBinding[];
}

/**
 * Internal configuration stored per tool in a ToolGroup.
 */
export interface ToolConfiguration {
    mode: ToolMode;
    bindings: ToolBinding[];
}

/**
 * Extract the modifier key combination from an InteractionEvent.
 */
export function getModifierFromEvent(event: InteractionEvent): ModifierKey {
    const { shiftKey, ctrlKey, altKey, metaKey } = event;

    if (shiftKey && ctrlKey) return ModifierKey.SHIFT_CTRL;
    if (shiftKey && altKey) return ModifierKey.SHIFT_ALT;
    if (ctrlKey && altKey) return ModifierKey.CTRL_ALT;
    if (shiftKey) return ModifierKey.SHIFT;
    if (ctrlKey) return ModifierKey.CTRL;
    if (altKey) return ModifierKey.ALT;
    if (metaKey) return ModifierKey.META;
    return ModifierKey.NONE;
}

/**
 * Map PointerEvent.button (0=left, 1=middle, 2=right) to MouseButton bitmask.
 */
export function mapButtonToMouseButton(button: number): MouseButton {
    switch (button) {
        case 0:
            return MouseButton.PRIMARY;
        case 1:
            return MouseButton.AUXILIARY;
        case 2:
            return MouseButton.SECONDARY;
        default:
            return MouseButton.PRIMARY;
    }
}
